import { type TimecodeSpec, TimecodeError } from "./framerate.js";

/** Tidskoden delt i sine fire tall. Bare til visning og parsing. */
export interface TimecodeParts {
  readonly hours: number;
  readonly minutes: number;
  readonly seconds: number;
  readonly frames: number;
}

/**
 * Ruller et framenummer inn i døgnet. Negative verdier ruller bakover, slik
 * en boks gjør — det er tilfellet der regi setter tidskoden etter at
 * loggingen har begynt.
 */
export function wrapFrames(frames: number, spec: TimecodeSpec): number {
  const d = spec.framesPerDay;
  return ((Math.trunc(frames) % d) + d) % d;
}

export function framesToParts(frames: number, spec: TimecodeSpec): TimecodeParts {
  let f = wrapFrames(frames, spec);
  const n = spec.rate.nominal;

  if (spec.dropFrame) {
    const d = spec.dropCount;
    const tens = Math.floor(f / spec.framesPer10Min);
    const rest = f % spec.framesPer10Min;
    f += d * 9 * tens;
    if (rest > d) {
      f += d * Math.floor((rest - d) / spec.framesPerMin);
    }
  }

  return {
    hours: Math.floor(f / (n * 3600)) % 24,
    minutes: Math.floor(f / (n * 60)) % 60,
    seconds: Math.floor(f / n) % 60,
    frames: f % n,
  };
}

/**
 * Sant når tidskoden er et framenummer drop-frame hopper over, og altså
 * ikke finnes. `00:01:00:00` på 29,97 DF er et slikt.
 */
export function isDroppedLabel(p: TimecodeParts, spec: TimecodeSpec): boolean {
  if (!spec.dropFrame) return false;
  return p.seconds === 0 && p.minutes % 10 !== 0 && p.frames < spec.dropCount;
}

export function partsToFrames(p: TimecodeParts, spec: TimecodeSpec): number {
  const n = spec.rate.nominal;
  if (
    !Number.isInteger(p.hours) || !Number.isInteger(p.minutes) ||
    !Number.isInteger(p.seconds) || !Number.isInteger(p.frames)
  ) {
    throw new TimecodeError("Tidskodens deler må være heltall");
  }
  if (p.hours < 0 || p.hours > 23) throw new TimecodeError(`Time utenfor døgnet: ${p.hours}`);
  if (p.minutes < 0 || p.minutes > 59) throw new TimecodeError(`Minutt utenfor området: ${p.minutes}`);
  if (p.seconds < 0 || p.seconds > 59) throw new TimecodeError(`Sekund utenfor området: ${p.seconds}`);
  if (p.frames < 0 || p.frames >= n) throw new TimecodeError(`Frame utenfor området: ${p.frames}`);
  if (isDroppedLabel(p, spec)) {
    throw new TimecodeError(
      `Tidskoden finnes ikke i drop-frame: ${p.hours}:${p.minutes}:${p.seconds}:${p.frames}`,
    );
  }

  let f = n * 3600 * p.hours + n * 60 * p.minutes + n * p.seconds + p.frames;
  if (spec.dropFrame) {
    const totalMinutes = 60 * p.hours + p.minutes;
    f -= spec.dropCount * (totalMinutes - Math.floor(totalMinutes / 10));
  }
  return f;
}

/**
 * Forløpt tid → frames. Bruker den faktiske frekvensen, ikke den nominelle.
 * Dette er det eneste stedet millisekunder blir til frames.
 */
export function framesFromElapsedMs(elapsedMs: number, spec: TimecodeSpec): number {
  return Math.round((elapsedMs * spec.rate.num) / (spec.rate.den * 1000));
}

/** Frames → forløpt tid. Til visning av differanser, ikke til lagring. */
export function elapsedMsFromFrames(frames: number, spec: TimecodeSpec): number {
  return Math.round((frames * spec.rate.den * 1000) / spec.rate.num);
}
